import { mountVeivoValidationFlow, VeivoValidationFlowHandle } from "@veivo/web-sdk";
import { useEffect, useRef, useState } from "react";
import { Typography } from "@/components/atoms";
import { VeivoHelpModal, VeivoHelpTopic } from "@/components/organisms/Vehicles/VeivoHelp/VeivoHelpModal";
import { DEFAULT_PRIMARY_COLOR } from "@/constants/branding";
import { VEIVO_BRAND_PARAM } from "@/helpers/veivoHandoffUrl";
import { brandFromLink, veivoThemeFromBranding } from "@/helpers/veivoTheme";

/**
 * Public (unauthenticated) host for the Veivo validation flow — the phone half
 * of the desktop→mobile QR handoff (DEV-1313).
 *
 * The wizard step lives behind Kinde auth, so scanning its QR at the car put
 * the driver on a login wall. This page carries no auth and no Redux: the SDK
 * reads `?veivoValidationId=` from the query and the short-lived session token
 * from the URL FRAGMENT (`#veivoSessionToken=`, never sent to a server, read
 * once then scrubbed), and every subsequent call goes straight to Veivo
 * carrying that token. Nothing here can reach Rideminder data.
 *
 * The service URL comes from build config, NOT the link. A base URL taken from
 * the QR would let a crafted link point a phone's camera upload at an attacker's
 * host, which is exactly the trust this page cannot extend.
 */
export const VeivoVerification = () => {
  const hostRef = useRef<HTMLDivElement>(null);
  const flowRef = useRef<VeivoValidationFlowHandle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [helpTopic, setHelpTopic] = useState<VeivoHelpTopic | null>(null);
  // The walk-around's ✕ (DEV-1806). This page has nowhere to send the user
  // back to, so it ends the flow and says how to pick it up again.
  const [left, setLeft] = useState(false);

  const baseUrl = import.meta.env.VITE_VEIVO_BASE_URL as string | undefined;

  /**
   * Read the link ONCE, on first render.
   *
   * The SDK strips the handoff code from the URL as soon as it redeems it — a
   * spent single-use credential has no business sitting in history. So these
   * cannot be recomputed per render: the code would read as absent the moment
   * after it was used, and an effect keyed on it would tear the flow down and
   * declare the link incomplete.
   *
   * The current QR carries only that code, which identifies the validation by
   * itself, so there is no id in the URL to look for. Older links still have one.
   *
   * It also carries the operator's primary colour (DEV-1806): this page has no
   * login, so no operator to ask. Only a plain hex colour is taken from it.
   */
  const [link] = useState(() => ({
    validationId: new URLSearchParams(window.location.search).get("veivoValidationId"),
    hasHandoffCode: new URLSearchParams(window.location.hash.slice(1)).has("h"),
    brand: brandFromLink(new URLSearchParams(window.location.search).get(VEIVO_BRAND_PARAM)),
  }));

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    if (!baseUrl) {
      setError("Vehicle verification is not configured for this environment.");
      return;
    }
    if (!link.validationId && !link.hasHandoffCode) {
      setError("This verification link is incomplete. Please re-scan the code shown on your computer.");
      return;
    }

    flowRef.current = mountVeivoValidationFlow(host, {
      service: { url: baseUrl },
      // The operator's buttons, as on the computer; RideMinder's purple for a
      // link without a colour (one made before links carried it).
      theme: veivoThemeFromBranding(link.brand ?? { primary: DEFAULT_PRIMARY_COLOR }),
      // Omitted for a code-only link: the SDK gets the id back from redeeming
      // the code.
      ...(link.validationId ? { resumeValidationId: link.validationId } : {}),
      // Testing affordance, build-time gated: renders a per-step skip control.
      // The service must ALSO have VEIVO_ALLOW_TEST_SKIP on, so this flag alone
      // cannot bypass anything — and it is unset in production builds.
      allowTestSkip: import.meta.env.VITE_VEIVO_ALLOW_TEST_SKIP === "true",
      onError: (err) => console.error("Veivo flow error:", err),
      // On a phone the walk-around is full-screen: without these it has no ✕
      // or ? at all.
      onClose: () => setLeft(true),
      onHelp: setHelpTopic,
    });

    return () => {
      void flowRef.current?.dispose();
      flowRef.current = null;
    };
  }, [baseUrl, link]);

  // Released here rather than inside the SDK's own ✕ handler, and for good:
  // the handoff code was single-use, so this page cannot resume the flow.
  useEffect(() => {
    if (!left) return;
    void flowRef.current?.dispose();
    flowRef.current = null;
  }, [left]);

  return (
    <div className="flex min-h-screen w-full flex-col bg-white">
      {error ? (
        <div className="flex flex-1 items-center justify-center p-6">
          <Typography variant="paragraph" className="text-center text-danger">
            {error}
          </Typography>
        </div>
      ) : left ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center" role="status">
          <Typography variant="title">You left the walk-around</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            To carry on, scan the QR code on the computer again, or choose Continue on the vehicle in My Vehicles. You can
            close this page.
          </Typography>
        </div>
      ) : (
        <div ref={hostRef} className="min-h-screen w-full" />
      )}

      <VeivoHelpModal topic={helpTopic} onClose={() => setHelpTopic(null)} />
    </div>
  );
};
